import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, filter, take, takeUntil } from 'rxjs';
import { ConnectedAccountsService } from 'shared/services/connected-accounts.service';
import { ConnectedAccountDto } from 'shared/services/api/connected-accounts.api.service';
import { SnackbarService } from 'shared/services/snackbar.service';
import { DialogService } from 'shared/services/dialog.service';
import { SettingsService } from 'shared/services/settings.service';

/**
 * Dialog zarządzania powiązanymi kontami:
 * - Zakładka 1: dodanie istniejącego konta (e-mail + hasło, opcjonalnie 2FA inline)
 * - Zakładka 2: lista powiązanych kont z akcjami "Przełącz" i "Odłącz"
 */
@Component({
    standalone: true,
    selector: 'add-new-account',
    templateUrl: './add-new-account.component.html',
    styleUrl: './add-new-account.component.scss',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatTabsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTooltipModule
    ]
})
export class AddNewAccountComponent implements OnInit, OnDestroy {

    private _destroy$ = new Subject<void>();

    public selectedTab = 0;
    public accounts: ConnectedAccountDto[] = [];
    public isLoadingList = false;

    // Aktualnie zalogowane konto (pokazywane na liście bez przycisków)
    public currentEmail: string | null = null;
    public currentCompanyName: string | null = null;

    public attachForm = new FormGroup({
        emailAddress: new FormControl<string>('', [Validators.required, Validators.email]),
        password: new FormControl<string>('', [Validators.required])
    });
    public isAttaching = false;
    public attachError: string | null = null;
    public showPassword = false;

    // Stan 2FA inline (po response.data.requiresTwoFactor=true)
    public twoFactorActive = false;
    public twoFactorMethod: 'email' | 'authenticator' | null = null;
    public twoFactorTempToken: string | null = null;
    public twoFactorMaskedEmail: string | null = null;
    public twoFactorCode = '';
    public is2faVerifying = false;
    public twoFactorError: string | null = null;

    constructor(
        public dialogRef: MatDialogRef<AddNewAccountComponent>,
        private _service: ConnectedAccountsService,
        private _snackbar: SnackbarService,
        private _dialogService: DialogService,
        private _settingsService: SettingsService
    ) { }

    ngOnInit(): void {
        this._loadAccounts();

        this._service.accounts$
            .pipe(takeUntil(this._destroy$))
            .subscribe(list => {
                this.accounts = list;
            });

        this._settingsService.currentUserSettings
            .pipe(takeUntil(this._destroy$))
            .subscribe((settings: any) => {
                this.currentEmail = settings?.user?.emailAddress
                    || settings?.emailAddress
                    || settings?.email
                    || null;
                this.currentCompanyName = settings?.company?.name
                    || settings?.company?.companyName
                    || null;
            });
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    public close(): void {
        this.dialogRef.close();
    }

    public submit(): void {
        if (this.attachForm.invalid) {
            this.attachForm.markAllAsTouched();
            return;
        }
        if (this.accounts.length >= 10) {
            this.attachError = 'Osiągnięto limit 10 powiązanych kont';
            return;
        }

        this.isAttaching = true;
        this.attachError = null;

        const { emailAddress, password } = this.attachForm.value;

        this._service.attach(emailAddress!, password!).subscribe({
            next: (response) => {
                this.isAttaching = false;

                if (!response?.success) {
                    this.attachError = response?.message || 'Nie udało się powiązać konta';
                    return;
                }

                if (response.data?.requiresTwoFactor) {
                    // Przejście do widoku 2FA inline.
                    this.twoFactorActive = true;
                    this.twoFactorMethod = response.data.method;
                    this.twoFactorTempToken = response.data.tempToken;
                    this.twoFactorMaskedEmail = response.data.maskedEmail || null;
                    return;
                }

                this._snackbar.showSnackBar('Konto zostało powiązane', 'success');
                this.attachForm.reset();
                this.selectedTab = 1;
            },
            error: (err) => {
                this.isAttaching = false;
                this.attachError = err?.error?.message || 'Wystąpił błąd';
            }
        });
    }

    public verify2fa(): void {
        if (!this.twoFactorTempToken || !this.twoFactorCode || this.twoFactorCode.length < 4) {
            this.twoFactorError = 'Wprowadź kod weryfikacyjny';
            return;
        }

        this.is2faVerifying = true;
        this.twoFactorError = null;

        this._service.verifyAttach2fa(this.twoFactorTempToken, this.twoFactorCode).subscribe({
            next: (response) => {
                this.is2faVerifying = false;

                if (!response?.success) {
                    this.twoFactorError = response?.message || 'Nieprawidłowy kod';
                    return;
                }

                this._snackbar.showSnackBar('Konto zostało powiązane', 'success');
                this._resetTwoFactor();
                this.attachForm.reset();
                this.selectedTab = 1;
            },
            error: (err) => {
                this.is2faVerifying = false;
                this.twoFactorError = err?.error?.message || 'Wystąpił błąd';
            }
        });
    }

    public cancelTwoFactor(): void {
        this._resetTwoFactor();
    }

    public switchTo(account: ConnectedAccountDto): void {
        this._service.switchTo(account).subscribe({
            next: (response) => {
                if (response?.success) {
                    this.dialogRef.close();
                }
            }
        });
    }

    public unlink(account: ConnectedAccountDto): void {
        this._dialogService.confirmDialog({
            title: 'Odłączyć powiązane konto?',
            message: `Konto <strong>${account.email}</strong> zostanie odłączone z obu stron. Aby przełączać się na nie ponownie, trzeba będzie powiązać je od nowa.`,
            type: 'warning',
            confirmText: 'Odłącz',
            cancelText: 'Anuluj'
        }).pipe(
            filter((confirmed): confirmed is boolean => confirmed !== null),
            take(1)
        ).subscribe(confirmed => {
            if (!confirmed) return;

            this._service.unlink(account._id).subscribe({
                next: (response) => {
                    if (response?.success) {
                        this._snackbar.showSnackBar('Konto zostało odłączone', 'success');
                    }
                }
            });
        });
    }

    /**
     * Inicjał do avatara — pierwsza litera nazwy firmy lub e-maila.
     */
    public getInitial(account: ConnectedAccountDto | { companyName?: string | null; email?: string | null }): string {
        const source = (account.companyName || account.email || '?').trim();
        return source.charAt(0).toUpperCase();
    }

    /**
     * Inicjał dla aktualnego konta.
     */
    public getCurrentInitial(): string {
        const source = (this.currentCompanyName || this.currentEmail || '?').trim();
        return source.charAt(0).toUpperCase();
    }

    private _loadAccounts(): void {
        this.isLoadingList = true;
        this._service.loadList().subscribe({
            next: () => { this.isLoadingList = false; },
            error: () => { this.isLoadingList = false; }
        });
    }

    private _resetTwoFactor(): void {
        this.twoFactorActive = false;
        this.twoFactorMethod = null;
        this.twoFactorTempToken = null;
        this.twoFactorMaskedEmail = null;
        this.twoFactorCode = '';
        this.twoFactorError = null;
    }
}
